import { forwardRef } from 'react';
import type { SelectHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Note: omitimos `size` do HTMLSelectAttributes (numérico) pra usar como variant string.
 */
interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  hint?: string;
  error?: string;
  /** Wraper sizing */
  size?: 'sm' | 'md' | 'lg';
  /**
   * UIUX-Comercial U1 (aditivo): classe do WRAPPER externo (default `w-full`).
   * Num flex-row, `className="w-40"` no <select> interno não adianta — o
   * wrapper block de largura cheia é quem quebra a linha (filtros empilhados).
   * Passe `wrapperClassName="w-40 shrink-0"` pra fixar a largura do item flex.
   */
  wrapperClassName?: string;
}

const SIZE_CLASSES = {
  sm: 'h-9 text-sm',
  md: 'h-11 text-sm',
  lg: 'h-12 text-base',
};

/**
 * Select — wrap do <select> nativo com estilo do design system.
 *
 * Usa <select> HTML nativo (não custom dropdown). Vantagens:
 *   - Acessibilidade automática (keyboard, screen reader)
 *   - UX mobile nativa (iOS/Android usam picker do sistema)
 *   - Zero JS overhead
 *
 * Uso:
 *   <Select defaultValue="">
 *     <option value="" disabled>Selecione...</option>
 *     <option value="comercial">Comercial</option>
 *     <option value="manutencao">Manutenção</option>
 *   </Select>
 *
 * Pra dropdowns customizados (com busca, multi-select, ícones), adicionaremos
 * Combobox em R1+ se precisar.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ hint, error, size = 'md', className, wrapperClassName, disabled, children, ...props }, ref) => {
    const hasError = !!error;

    return (
      <div className={wrapperClassName ?? 'w-full'}>
        <div className="relative">
          <select
            ref={ref}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            className={cn(
              'w-full appearance-none rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1',
              'border border-line dark:border-line-dark px-4 pr-10',
              'focus:outline-none focus:ring-2 transition-colors',
              SIZE_CLASSES[size],
              hasError
                ? 'border-red focus:border-red focus:ring-red-soft dark:focus:ring-red-soft-dark'
                : 'focus:border-red focus:ring-red-soft dark:focus:ring-red-soft-dark',
              disabled && 'opacity-60 cursor-not-allowed',
              className
            )}
            {...props}
          >
            {children}
          </select>
          <Chevron />
        </div>
        {(hint || error) && (
          <p className={cn('text-xs mt-1.5', hasError ? 'text-red' : 'text-ink-3 dark:text-ink-dark-3')}>
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);
Select.displayName = 'Select';

function Chevron() {
  return (
    <svg
      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3 pointer-events-none"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Exports auxiliares pra composição (não obrigatórios, mas padronizam API)
export function SelectOption(props: { value: string; disabled?: boolean; children: ReactNode }) {
  return <option value={props.value} disabled={props.disabled}>{props.children}</option>;
}
